export type PropertyDict = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export enum MixpanelEvent {
  // Account
  AccountDeletionCancelled = "account_deletion_cancelled",
  AccountDeletionRequested = "account_deletion_requested",
  DeleteAccountModalClosed = "delete_account_modal_closed",
  DeleteAccountModalOpened = "delete_account_modal_opened",
  PasswordChanged = "password_changed",
  PasswordResetRequested = "password_reset_requested",

  // Authentication
  AnonymousUserRegistered = "anonymous_user_registered",
  UserSignedIn = "user_signed_in",
  UserSignedOut = "user_signed_out",
  UserSignedUp = "user_signed_up",

  // Billing
  BillingIntervalChanged = "billing_interval_changed",
  MeterDecremented = "meter_decremented",
  MeterIncremented = "meter_incremented",
  UpgradeButtonClicked = "upgrade_button_clicked",
  UpgradeModalClosed = "upgrade_modal_closed",
  UpgradeModalOpened = "upgrade_modal_opened",
  PromptLimitReached = "prompt_limit_reached",

  // Canvas
  SelectionMoved = "selection_moved",
  SelectionResized = "selection_resized",

  // Collaboration
  CollaborationModalClosed = "collaboration_modal_closed",
  CollaborationModalOpened = "collaboration_modal_opened",
  CollaboratorAccessUpdated = "collaborator_access_updated",
  CollaboratorConnected = "collaborator_connected",
  CollaboratorDisconnected = "collaborator_disconnected",
  CollaboratorFollowFocused = "collaborator_follow_focused",
  CollaboratorFollowStarted = "collaborator_follow_started",
  CollaboratorFollowStopped = "collaborator_follow_stopped",
  CollaboratorReconnected = "collaborator_reconnected",
  InvitationAccepted = "invitation_accepted",
  InvitationSent = "invitation_sent",
  PublicAccessUpdated = "public_access_updated",
  PublicLinkCopied = "public_link_copied",

  // Export
  ExportModalClosed = "export_modal_closed",
  ExportModalOpened = "export_modal_opened",
  ImageAreaChanged = "image_area_changed",
  ImageExported = "image_exported",
  ImageFormatChanged = "image_format_changed",
  ImageGroupingChanged = "image_grouping_changed",
  ImageIncludeBackgroundChanged = "image_include_background_changed",

  // File
  FileCreated = "file_created",
  FileDeleted = "file_deleted",
  FileRenamed = "file_renamed",
  FileSelected = "file_selected",
  FileViewChanged = "file_view_changed",

  // Generation
  ArtifactCompleted = "artifact_completed",
  ServiceCompleted = "service_completed",

  // Global
  GlobalActionExecuted = "global_action_executed",

  // History
  ActionRedone = "action_redone",
  ActionUndone = "action_undone",

  // Main menu
  MainMenuClosed = "main_menu_closed",
  MainMenuOpened = "main_menu_opened",

  // MCP
  McpClientConnected = "mcp_client_connected",
  McpToolCalled = "mcp_tool_called",
  McpPromoCardViewed = "mcp_promo_card_viewed",
  McpPromoCardClicked = "mcp_promo_card_clicked",
  McpPromoCardDismissed = "mcp_promo_card_dismissed",

  // Mobile
  DesktopRecommendationIgnored = "desktop_recommendation_ignored",
  MobileModalClosed = "mobile_modal_closed",
  MobileModalOpened = "mobile_modal_opened",

  // Onboarding
  OnboardingDismissed = "onboarding_dismissed",
  PromptSelected = "prompt_selected",

  // Options menu
  ExpandMenuClosed = "expand_menu_closed",
  ExpandMenuOpened = "expand_menu_opened",
  OptionsMenuActionExecuted = "options_menu_action_executed",

  // Prototype
  PrototypeButtonDisplayed = "prototype_button_displayed",
  PrototypeButtonClicked = "prototype_button_clicked",

  // Prompt panel
  PromptAttachmentAdded = "prompt_attachment_added",
  PromptAttachmentDownloaded = "prompt_attachment_downloaded",
  PromptAttachmentRemoved = "prompt_attachment_removed",
  PromptCancelled = "prompt_cancelled",
  PromptSubmitted = "prompt_submitted",
  PromptTargetsClicked = "prompt_targets_clicked",

  // Property panels
  CodeCopied = "code_copied",
  CodeDownloaded = "code_downloaded",
  PropertyPanelClosed = "property_panel_closed",
  PropertyPanelOpened = "property_panel_opened",
  TextEdited = "text_edited",

  // Referrals
  ReferralAffiliateCreated = "referral_affiliate_created",

  // Toolbar
  ToolChanged = "tool_changed",

  // Zoom
  ZoomedIn = "zoomed_in",
  ZoomedOut = "zoomed_out",
  ZoomedTo100 = "zoomed_to_100",
  ZoomedToFit = "zoomed_to_fit",
  ZoomedToSelection = "zoomed_to_selection",
  ZoomMenuClosed = "zoom_menu_closed",
  ZoomMenuOpened = "zoom_menu_opened",

  // Web page view
  WebPageViewed = "$mp_web_page_view",
}

export enum EventGroup {
  Account = "account",
  Authentication = "authentication",
  Billing = "billing",
  Canvas = "canvas",
  Collaboration = "collaboration",
  Export = "export",
  File = "file",
  Generation = "generation",
  Global = "global",
  History = "history",
  MainMenu = "main_menu",
  Mcp = "mcp",
  Mobile = "mobile",
  Onboarding = "onboarding",
  OptionsMenu = "options_menu",
  PromptPanel = "prompt_panel",
  Prototype = "prototype",
  PropertyPanels = "property_panels",
  Referrals = "referrals",
  Toolbar = "toolbar",
  Zoom = "zoom",
  WebPageView = "web_page_view",
}

export enum GenerationMode {
  Single = "single",
  Multiple = "multiple",
}

export enum GenerationService {
  ScreenGenerate = "screen.generate",
  ScreenRegenerate = "screen.regenerate",
  ScreenExpand = "screen.expand",
  ScreenEdit = "screen.edit",
}

export enum GlobalAction {
  Copy = "copy",
  Cut = "cut",
  Paste = "paste",
  SelectAll = "select_all",
}

export enum OptionsMenuAction {
  RegenerateDifferentLayout = "regenerate_different_layout",
  RegenerateDifferentStyle = "regenerate_different_style",
  RegenerateFromScratch = "regenerate_from_scratch",
  ExpandNextScreen = "expand_next_screen",
  ExpandErrorState = "expand_error_state",
  ExpandEmptyState = "expand_empty_state",
  ExpandWebVersion = "expand_web_version",
  ExpandMobileVersion = "expand_mobile_version",
  ExpandTabletVersion = "expand_tablet_version",
  ExpandMobileIosVersion = "expand_mobile_ios_version",
  ExpandMobileAndroidVersion = "expand_mobile_android_version",
  EditDarkTheme = "edit_dark_theme",
  EditLightTheme = "edit_light_theme",
  EditMakePretty = "edit_make_pretty",
  SetTheme = "set_theme",
  SetLightAppearance = "set_light_appearance",
  SetDarkAppearance = "set_dark_appearance",
  DownloadAsPNG = "download_as_png",
  ExportToFigma = "export_to_figma",
  ExportToCode = "export_to_code",
  SendToAgent = "send_to_agent",
  Duplicate = "duplicate",
  Delete = "delete",
}

export enum PanelToggleSource {
  PanelButton = "panel_button",
  Shortcut = "shortcut",
  MainMenu = "main_menu",
}

export enum WebPageViewApplication {
  Copilot = "copilot",
  Editor = "editor",
}
